/**
 * Alatreyu Cart Drawer
 * 
 * A custom cart drawer with:
 * - Product recommendations via metafields (fetched via section rendering)
 * - 50/50 shipping split calculation
 * - Sample kit purchase limits
 * - Pay What You Can variant selection
 */
(function() {
  'use strict';

  // Prevent double initialization
  if (window.__AlatreyuCartDrawerInit) return;
  window.__AlatreyuCartDrawerInit = true;

  // Selectors
  const S = {
    drawer: '#alatreyu-cart-drawer',
    overlay: '.alatreyu-cart-drawer__overlay',
    close: '.alatreyu-cart-drawer__close',
    toggle: '#alatreyu-cart-toggle',
    count: '#alatreyu-cart-count',
    items: '#alatreyu-cart-items',
    footer: '#alatreyu-cart-footer',
    upsells: '#alatreyu-cart-upsells',
    upsellsList: '#alatreyu-upsells-list',
    shipping: '#alatreyu-cart-shipping',
    shippingOrig: '#alatreyu-shipping-original',
    shippingPay: '#alatreyu-shipping-you-pay',
    subtotal: '#alatreyu-cart-subtotal',
    empty: '#alatreyu-cart-empty',
    tiersData: '#alatreyu-shipping-tiers-data',
    settingsData: '#alatreyu-cart-drawer-settings',
    checkoutBtn: '#alatreyu-checkout-btn',
    alertModal: '#alatreyu-checkout-alert-modal',
    alertCancel: '#alatreyu-alert-cancel',
    alertContinue: '#alatreyu-alert-continue'
  };

  // State
  let cart = null;
  let releaseCartTrap = null;

  // Focus trap utility
  const CART_FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function cartTrapFocus(container) {
    function handler(e) {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(container.querySelectorAll(CART_FOCUSABLE)).filter(el => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  // Helper: convert Shopify cart items to GA4 ecommerce items array
  function cartItemsToGA4(items) {
    return (items || []).map(i => ({
      item_id: i.sku || '',
      item_name: i.product_title || '',
      item_brand: 'Alatreyu',
      price: i.price / 100,
      quantity: i.quantity
    }));
  }
  let tiers = [];
  let settings = {};
  let shipCost = 0;
  let refreshToken = 0;

  // Zone-aware shipping state
  let userZone = null;
  let userState = null;
  let zoneRates = null;

  // Initialize
  function init() {
    const drawer = document.querySelector(S.drawer);
    if (!drawer) return;

    loadSettings();
    loadTiers();
    bindEvents();
    loadZoneRates();
    detectLocation();

    // Initial load
    refreshCart();
  }

  function loadSettings() {
    const el = document.querySelector(S.settingsData);
    if (el) {
      try {
        settings = JSON.parse(el.textContent) || {};
      } catch (e) { /* silently ignore parse failures */ }
    }
  }

  function loadTiers() {
    const el = document.querySelector(S.tiersData);
    if (el) {
      try {
        tiers = JSON.parse(el.textContent) || [];
      } catch (e) { /* silently ignore parse failures */ }
    }
  }

  function resolveZone() {
    // Try to resolve userZone from userState + zoneRates
    if (userState && !userZone && zoneRates && zoneRates.state_to_zone) {
      userZone = zoneRates.state_to_zone[userState] || null;
      if (userZone) {
        try {
          sessionStorage.setItem('alatreyuShipZone',
            JSON.stringify({ zone: userZone, state: userState }));
        } catch (e) { /* ignore */ }
      }
    }
  }

  function loadZoneRates() {
    var urlEl = document.querySelector('#alatreyu-shipping-zones-url');
    if (!urlEl) return;
    var url = urlEl.textContent.trim();
    if (!url) return;
    fetch(url)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        zoneRates = data;
        // Resolve zone if geolocation arrived first
        resolveZone();
        if (cart) calculateShipping();
      })
      .catch(function() { /* use fallback tiers */ });
  }

  function detectLocation() {
    // Check sessionStorage cache first
    try {
      var cached = sessionStorage.getItem('alatreyuShipZone');
      if (cached) {
        var data = JSON.parse(cached);
        userZone = data.zone;
        userState = data.state;
        if (cart) calculateShipping();
        return;
      }
    } catch (e) { /* ignore */ }

    // Fetch from IP geolocation API
    fetch('https://front.4147r3yu.com/api/geo/location')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.region_code && data.country_code === 'US') {
          userState = data.region_code;
          // Resolve zone if rate data already loaded
          resolveZone();
          if (userState && userZone) {
            try {
              sessionStorage.setItem('alatreyuShipZone',
                JSON.stringify({ zone: userZone, state: userState }));
            } catch (e) { /* ignore */ }
          }
          if (cart) calculateShipping();
        }
      })
      .catch(function() { /* fallback to national average */ });
  }

  function bindEvents() {
    // Toggle button
    const toggle = document.querySelector(S.toggle);
    if (toggle) toggle.addEventListener('click', open);

    // Close button
    const close = document.querySelector(S.close);
    if (close) close.addEventListener('click', closeDrawer);

    // Overlay click
    const overlay = document.querySelector(S.overlay);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    // Nav links (Contact, FAQ) — close drawer and navigate
    document.querySelectorAll('.alatreyu-cart-drawer__nav-link').forEach(link => {
      link.addEventListener('click', () => closeDrawer());
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeDrawer();
    });

    // Intercept cart links
    document.addEventListener('click', (e) => {
      // Cart buttons
      const cartBtn = e.target.closest('.cart-button, [aria-controls*="cart"], [data-cart-toggle], a[href="/cart"]:not([data-no-drawer])');
      if (cartBtn && !e.target.closest('.alatreyu-cart-drawer')) {
        e.preventDefault();
        e.stopPropagation();
        open();
        return;
      }
    }, true);

    // Intercept add to cart forms
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (form) {
        e.preventDefault();
        await addFromForm(form);
      }
    });

    // Checkout button
    const checkoutBtn = document.querySelector(S.checkoutBtn);
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', handleCheckout);
    }

    // Alert modal buttons
    const alertCancel = document.querySelector(S.alertCancel);
    const alertContinue = document.querySelector(S.alertContinue);
    if (alertCancel) alertCancel.addEventListener('click', hideAlert);
    if (alertContinue) alertContinue.addEventListener('click', () => {
      hideAlert();
      window.location.href = '/checkout';
    });

    // Delegate events for dynamic content
    document.addEventListener('click', handleDynamicClick);

    // Close dropdown on scroll (fixed panel doesn't scroll with content)
    const content = document.querySelector('.alatreyu-cart-drawer__content');
    if (content) content.addEventListener('scroll', closeAllUpsellDropdowns);

    // Listen for cart updates from other scripts
    document.addEventListener('cart:refresh', refreshCart);
  }

  function handleDynamicClick(e) {
    // Quantity buttons
    const qtyBtn = e.target.closest('.alatreyu-cart-item__quantity-btn');
    if (qtyBtn) {
      e.preventDefault();
      handleQuantityChange(qtyBtn);
      return;
    }

    // Remove button
    const removeBtn = e.target.closest('.alatreyu-cart-item__remove');
    if (removeBtn) {
      e.preventDefault();
      handleRemove(removeBtn);
      return;
    }

    // Upsell toggle
    const upsellToggle = e.target.closest('.alatreyu-upsell-card__toggle');
    if (upsellToggle) {
      e.preventDefault();
      handleUpsellToggle(upsellToggle);
      return;
    }

    // Upsell PWYC dropdown trigger
    const dropTrigger = e.target.closest('.upsell-pwyc-dropdown__trigger');
    if (dropTrigger) {
      e.preventDefault();
      const dd = dropTrigger.closest('.upsell-pwyc-dropdown');
      if (dd) {
        const wasOpen = dd.classList.contains('open');
        closeAllUpsellDropdowns();
        if (!wasOpen) {
          dd.classList.add('open');
          positionDropdownPanel(dd, dropTrigger);
        }
      }
      return;
    }

    // Upsell PWYC dropdown option
    const dropOption = e.target.closest('.upsell-pwyc-dropdown__option');
    if (dropOption && !dropOption.disabled) {
      e.preventDefault();
      handleUpsellDropdownSelect(dropOption);
      return;
    }

    // Upsell add
    const upsellAdd = e.target.closest('.alatreyu-upsell-card__expand-add');
    if (upsellAdd) {
      e.preventDefault();
      handleUpsellAdd(upsellAdd);
      return;
    }
  }

  // Open/Close

  /** Show the drawer panel, set up focus trap, fire GA4 view_cart. */
  function showDrawerPanel() {
    const drawer = document.querySelector(S.drawer);
    if (!drawer) return;
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const panel = drawer.querySelector('.alatreyu-cart-drawer__panel');
    if (panel) {
      releaseCartTrap = cartTrapFocus(panel);
      const closeBtn = panel.querySelector('.alatreyu-cart-drawer__close');
      if (closeBtn) closeBtn.focus();
    }
    /* GA4 view_cart event */
    if (typeof gtag === 'function' && cart) {
      gtag('event', 'view_cart', {
        currency: 'USD',
        value: cart.total_price / 100,
        items: cartItemsToGA4(cart.items)
      });
    }
    observeUpsellImpressions();
  }

  /** Open the drawer immediately and refresh cart data in the background. */
  function open() {
    showDrawerPanel();
    refreshCart();
  }

  /**
   * Open the cart drawer after refreshing cart data.
   * Used by add-to-cart flows to avoid the "Your cart is empty" flash.
   * Hides the empty state immediately, awaits the cart refresh, then opens
   * with items already rendered.
   */
  async function openAfterRefresh() {
    // Immediately hide the empty state so the drawer won't flash
    // "Your cart is empty" while the fetch is in flight.
    const empty = document.querySelector(S.empty);
    const footer = document.querySelector(S.footer);
    if (empty) empty.style.display = 'none';
    if (footer) footer.style.display = 'block';

    // Refresh cart data first — this populates items + updates UI
    await refreshCart();

    // Now open the drawer with the data already rendered
    showDrawerPanel();
  }

  function closeDrawer() {
    const drawer = document.querySelector(S.drawer);
    if (releaseCartTrap) { releaseCartTrap(); releaseCartTrap = null; }
    if (drawer) {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      // Collapse any expanded upsells
      drawer.querySelectorAll('.alatreyu-upsell-card.is-expanded').forEach(card => {
        card.classList.remove('is-expanded');
      });
    }
    resetUpsellImpressions();
  }

  // Cart operations
  async function refreshCart() {
    const token = ++refreshToken;
    try {
      // Fetch cart data
      const cartRes = await fetch('/cart.js');
      if (!cartRes.ok || token !== refreshToken) return;
      cart = await cartRes.json();

      // Fetch rendered cart items via section rendering
      const sectionsRes = await fetch('/?sections=alatreyu-cart-items,alatreyu-cart-recommendations');
      if (token !== refreshToken) return;
      const sections = await sectionsRes.json();

      // Update cart items
      const itemsContainer = document.querySelector(S.items);
      if (itemsContainer && sections['alatreyu-cart-items']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['alatreyu-cart-items'], 'text/html');
        const newItems = doc.querySelector('#alatreyu-cart-items-data');
        if (newItems) {
          itemsContainer.innerHTML = newItems.innerHTML;
        }
      }

      // Update recommendations
      if (settings.showRecommendations) {
        const upsellsList = document.querySelector(S.upsellsList);
        const upsellsContainer = document.querySelector(S.upsells);
        if (upsellsList && sections['alatreyu-cart-recommendations']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections['alatreyu-cart-recommendations'], 'text/html');
          const newRecs = doc.querySelector('#alatreyu-recommendations-data');
          if (newRecs && newRecs.children.length > 0) {
            upsellsList.innerHTML = newRecs.innerHTML;
            upsellsContainer.style.display = 'block';
          } else {
            upsellsContainer.style.display = 'none';
          }
        }
      }

      // Update UI
      updateCartUI();
      calculateShipping();
      checkSampleLimits();

      // Cards were just re-rendered — re-observe them for impressions.
      observeUpsellImpressions();

    } catch (err) { /* refresh failed silently */ }
  }

  function updateCartUI() {
    const footer = document.querySelector(S.footer);
    const empty = document.querySelector(S.empty);
    const countEl = document.querySelector(S.count);
    const subtotalEl = document.querySelector(S.subtotal);

    if (!cart) return;

    // Show/hide empty state
    if (cart.item_count === 0) {
      if (footer) footer.style.display = 'none';
      if (empty) empty.style.display = 'flex';
    } else {
      if (footer) footer.style.display = 'block';
      if (empty) empty.style.display = 'none';
    }

    // Update count
    if (countEl) {
      countEl.textContent = cart.item_count;
      countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
    }

    // Update subtotal (including shipping)
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price + shipCost);
    }
  }

  function calculateShipping() {
    const shippingEl = document.querySelector(S.shipping);
    const origEl = document.querySelector(S.shippingOrig);
    const payEl = document.querySelector(S.shippingPay);
    const locEl = document.querySelector('#alatreyu-shipping-location');

    shipCost = 0;

    if (!settings.showShipping || !shippingEl || !cart || cart.item_count === 0) {
      if (shippingEl) shippingEl.style.display = 'none';
      return;
    }

    // Calculate total weight in oz
    const weightOz = cart.items.reduce((sum, item) => {
      return sum + (item.grams * item.quantity) / 28.35;
    }, 0);

    // Use zone-specific tiers if available, otherwise fall back to national average / settings tiers
    var activeTiers = tiers;
    if (userZone && zoneRates && zoneRates.zones && zoneRates.zones[userZone]) {
      activeTiers = zoneRates.zones[userZone].tiers;
    } else if (zoneRates && zoneRates.national_average) {
      activeTiers = zoneRates.national_average;
    }

    // Find matching tier
    let tier = activeTiers.find(t => weightOz >= t.min && weightOz < t.max);
    if (!tier && activeTiers.length) {
      tier = activeTiers[activeTiers.length - 1]; // Use highest tier
    }

    if (tier) {
      shippingEl.classList.toggle('is-free', !tier.full);
      if (tier.full) {
        shipCost = tier.pays;
        if (origEl) origEl.textContent = formatMoney(tier.full);
        if (payEl) payEl.textContent = formatMoney(tier.pays);
      } else {
        if (payEl) payEl.textContent = 'FREE';
      }
      shippingEl.style.display = 'block';
    } else {
      shippingEl.style.display = 'none';
    }

    // Location element hidden — zone data used silently for rate lookup

    // Update subtotal with shipping
    const subtotalEl = document.querySelector(S.subtotal);
    if (subtotalEl && cart) {
      subtotalEl.textContent = formatMoney(cart.total_price + shipCost);
    }
  }

  function checkSampleLimits() {
    if (!settings.sampleLimitEnabled || !cart) return;

    const pattern = (settings.sampleSkuPattern || 'FSP').toUpperCase();
    const limit = settings.sampleLimit || 3;

    // Count only FREE sample kits (price = 0). Paid additional kits are unlimited.
    let count = 0;
    cart.items.forEach(item => {
      const sku = (item.sku || '').toUpperCase();
      if (sku.includes(pattern) && item.price === 0) {
        count += item.quantity;
      }
    });

    const overLimit = count > limit;

    // Show/hide warnings
    document.querySelectorAll('.alatreyu-cart-item__sample-warning').forEach(warning => {
      warning.style.display = overLimit ? 'flex' : 'none';
    });

    // Enable/disable checkout
    const checkoutBtn = document.querySelector(S.checkoutBtn);
    if (checkoutBtn) {
      checkoutBtn.disabled = overLimit;
      checkoutBtn.classList.toggle('is-disabled', overLimit);
    }
  }

  // Quantity change
  async function handleQuantityChange(btn) {
    const key = btn.dataset.key;
    const action = btn.dataset.action;
    const item = cart?.items.find(i => i.key === key);
    if (!item) return;

    let quantity = item.quantity;
    if (action === 'increase') quantity++;
    else if (action === 'decrease') quantity = Math.max(0, quantity - 1);

    if (window.AlatreyuHaptics) window.AlatreyuHaptics('tap');
    await updateCartItem(key, quantity);
  }

  // Remove item
  async function handleRemove(btn) {
    const key = btn.dataset.key;
    const itemEl = btn.closest('.alatreyu-cart-item');

    // Publish remove event for Custom Pixel analytics
    if (typeof Shopify !== 'undefined' && Shopify.analytics?.publish && cart) {
      const item = cart.items.find(i => i.key === key);
      if (item) {
        Shopify.analytics.publish('cart_item_removed', {
          product_title: item.product_title || '',
          variant_id: item.variant_id,
          quantity: item.quantity,
          price: item.price
        });
      }
    }
    // GA4 remove_from_cart event
    if (typeof gtag === 'function' && cart) {
      const item = cart.items.find(i => i.key === key);
      if (item) {
        gtag('event', 'remove_from_cart', {
          currency: 'USD',
          value: item.price / 100 * item.quantity,
          items: [{
            item_id: item.sku || '',
            item_name: item.product_title || '',
            item_brand: 'Alatreyu',
            price: item.price / 100,
            quantity: item.quantity
          }]
        });
      }
    }

    // Animate removal
    if (itemEl) {
      itemEl.classList.add('is-removing');
      await new Promise(r => setTimeout(r, 300));
    }

    await updateCartItem(key, 0);
  }

  async function updateCartItem(key, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      await refreshCart();
    } catch (err) { /* update failed silently */ }
  }

  // Add to cart
  async function addFromForm(form) {
    try {
      // Use FormData directly to preserve all fields including gift card
      // recipient properties (Recipient email, Recipient name, Message, etc.)
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form)
      });
      /* These two never read the response at all. The drawer refreshes and
         opens on the TRUE cart either way, so unlike the pwyc path the customer
         is not told a lie -- but a sold-out 422 was completely invisible,
         including to anyone debugging why an add "did nothing". Keep the
         refresh, because showing the real cart is the honest outcome, and make
         the failure visible. */
      if (!res.ok) console.warn('[cart-drawer] /cart/add.js failed', res.status);
      await refreshCart();
      showDrawerPanel();
    } catch (err) { /* add-to-cart failed silently */ }
  }

  async function addToCart(id, quantity = 1) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity })
      });
      if (!res.ok) console.warn('[cart-drawer] /cart/add.js failed', res.status);
      await refreshCart();
      showDrawerPanel();
    } catch (err) { /* add-to-cart failed silently */ }
  }

  /* ── GA4 upsell impressions ──────────────────────────────────────────
     Fires cart_upsell_impression once per card per drawer-open, when the card
     actually scrolls into view. Without this the adds have no denominator and
     slot CTR is not computable: CTR = cart_upsell_added / cart_upsell_impression.
     Params mirror cart_upsell_added exactly so both group by the same
     already-registered event-scoped custom dimensions.

     Only fires while the drawer is open — refreshCart() also runs on page load
     with the drawer closed, and those cards are never seen.
  */
  let upsellObserver = null;
  const seenUpsells = new Set();

  /** 1-based slot index of an upsell card within its list. */
  function upsellPosition(card) {
    const list = card.parentElement;
    return list ? Array.from(list.children).indexOf(card) + 1 : 0;
  }

  function upsellEventParams(card) {
    const titleEl = card.querySelector('.alatreyu-upsell-card__title');
    const trigger = card.querySelector('.upsell-pwyc-dropdown__trigger');
    return {
      item_name: titleEl ? titleEl.textContent.trim() : '',
      item_id: card.getAttribute('data-product-id') || '',
      item_handle: card.getAttribute('data-handle') || '',
      variant_id: trigger ? (trigger.getAttribute('data-variant-id') || '') : '',
      position: upsellPosition(card),
      cart_value: cart?.total_price || 0,
      cart_item_count: cart?.item_count || 0
    };
  }

  /** Observe currently-rendered upsell cards; fire an impression once each. */
  function observeUpsellImpressions() {
    if (typeof gtag !== 'function' || !('IntersectionObserver' in window)) return;

    const drawer = document.querySelector(S.drawer);
    if (!drawer || !drawer.classList.contains('is-open')) return;

    const container = document.querySelector(S.upsells);
    if (!container || container.style.display === 'none') return;

    if (!upsellObserver) {
      upsellObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const card = entry.target;
          upsellObserver.unobserve(card);
          // Key by handle+slot: a card genuinely re-seen in a new slot counts again.
          const key = (card.getAttribute('data-handle') || '') + '@' + upsellPosition(card);
          if (seenUpsells.has(key)) return;
          seenUpsells.add(key);
          gtag('event', 'cart_upsell_impression', upsellEventParams(card));
        });
      }, { threshold: 0.5 });
    }

    document.querySelectorAll(S.upsellsList + ' .alatreyu-upsell-card')
      .forEach(card => upsellObserver.observe(card));
  }

  /** Reset impression dedupe so the next drawer-open counts fresh. */
  function resetUpsellImpressions() {
    if (upsellObserver) upsellObserver.disconnect();
    seenUpsells.clear();
  }

  // Upsell handlers
  function handleUpsellToggle(btn) {
    const card = btn.closest('.alatreyu-upsell-card');
    if (card) {
      const wasExpanded = card.classList.contains('is-expanded');
      card.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', !wasExpanded);
      
      if (!wasExpanded) {
        // Scroll to show expansion
        setTimeout(() => {
          card.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 320);
      }
    }
  }

  function closeAllUpsellDropdowns() {
    // Return portaled panels to their original dropdown parents
    document.querySelectorAll('.alatreyu-cart-drawer__panel > .upsell-pwyc-dropdown__panel').forEach(panel => {
      if (panel._dropdown) {
        panel._dropdown.appendChild(panel);
        panel._dropdown.classList.remove('open');
        panel._dropdown = null;
        panel._trigger = null;
        panel.style.cssText = '';
      }
    });
    // Close any non-portaled dropdowns
    document.querySelectorAll('.upsell-pwyc-dropdown.open').forEach(dd => {
      dd.classList.remove('open');
    });
  }

  function positionDropdownPanel(dd, trigger) {
    const panel = dd.querySelector('.upsell-pwyc-dropdown__panel');
    if (!panel) return;

    const drawerPanel = trigger.closest('.alatreyu-cart-drawer__panel');
    if (!drawerPanel) return;

    const rect = trigger.getBoundingClientRect();
    const dpRect = drawerPanel.getBoundingClientRect();

    // Store references for event handling and cleanup
    panel._dropdown = dd;
    panel._trigger = trigger;

    // Portal panel to drawer panel to escape content overflow clipping
    drawerPanel.appendChild(panel);
    panel.style.position = 'absolute';
    panel.style.top = (rect.bottom - dpRect.top) + 'px';
    panel.style.left = (rect.left - dpRect.left) + 'px';
    panel.style.width = rect.width + 'px';
    panel.style.display = 'block';
    panel.style.zIndex = '100';
  }

  function handleUpsellDropdownSelect(option) {
    const panel = option.closest('.upsell-pwyc-dropdown__panel');
    // Panel may be portaled — use stored references
    const dd = panel?._dropdown || option.closest('.upsell-pwyc-dropdown');
    const trigger = panel?._trigger || dd?.querySelector('.upsell-pwyc-dropdown__trigger');
    if (!panel || !dd) return;

    // Update active state
    panel.querySelectorAll('.upsell-pwyc-dropdown__option').forEach(o => o.classList.remove('active'));
    option.classList.add('active');

    // Update trigger text and stored variant
    if (trigger) {
      trigger.textContent = option.textContent.trim();
      trigger.setAttribute('data-variant-id', option.getAttribute('data-variant-id'));
    }

    // Close dropdown (returns panel to original parent)
    closeAllUpsellDropdowns();
  }

  async function handleUpsellAdd(btn) {
    const card = btn.closest('.alatreyu-upsell-card');
    const trigger = card?.querySelector('.upsell-pwyc-dropdown__trigger');
    const variantId = trigger?.getAttribute('data-variant-id');

    if (!variantId) return;

    if (window.AlatreyuHaptics) window.AlatreyuHaptics('tap');
    btn.disabled = true;
    btn.textContent = '...';

    // Collapse and animate out
    card.classList.remove('is-expanded');
    card.style.opacity = '0.5';

    // GA4: the numerator for slot CTR (denominator = cart_upsell_impression).
    if (typeof gtag === 'function') {
      gtag('event', 'cart_upsell_added', {
        ...upsellEventParams(card),
        variant_id: variantId  // the variant actually chosen in the dropdown
      });
    }

    await addToCart(variantId, 1);

    // Card will be removed on refresh since product is now in cart
  }

  // Checkout
  function handleCheckout(e) {
    e.preventDefault();

    const btn = document.querySelector(S.checkoutBtn);
    if (btn?.disabled) return;

    // Publish checkout initiation for Custom Pixel analytics
    if (typeof Shopify !== 'undefined' && Shopify.analytics?.publish && cart) {
      Shopify.analytics.publish('checkout_initiated', {
        item_count: cart.item_count,
        total_price: cart.total_price,
        items: cart.items.map(i => ({
          product_title: i.product_title,
          variant_id: i.variant_id,
          quantity: i.quantity,
          price: i.price
        }))
      });
    }
    // begin_checkout is handled by Shopify's built-in Customer Events pixel

    if (settings.checkoutAlertEnabled && settings.checkoutAlertMessage) {
      showAlert();
    } else {
      window.location.href = '/checkout';
    }
  }

  function showAlert() {
    const modal = document.querySelector(S.alertModal);
    if (modal && typeof modal.showModal === 'function') modal.showModal();
  }

  function hideAlert() {
    const modal = document.querySelector(S.alertModal);
    if (modal && typeof modal.close === 'function') modal.close();
  }

  // Utilities
  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(cents / 100);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API
  window.AlatreyuCartDrawer = {
    open,
    openAfterRefresh,
    close: closeDrawer,
    refresh: refreshCart
  };

})();
